//jshint esversion:6
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import BackgroundSVG from './SVG/BackgroundSVG'
import HabitRow from './HabitRow'
import NewHabitForm from './NewHabitForm'

import { postHabit } from '../../../actions/habits'
import { Habit, StoredHabit } from './habits.model'
import { SUCCESS_FINISH_COLOR } from '../../../consts/consts'
import { TableSkeleton } from '../../../shared/skeletons'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { habitsActions, habitsActionsThunk, selectActiveHabits } from '../../../store/habitsSlice'
import { makeDaysArray } from '../../../services/effects.service'
import { getTodayKey } from '../../../services/dates.service'
import { authActions } from '../../../store/authSlice'
import { onboard } from '../../../actions/auth'
import OnboardingModal from './Onboarding/OnboardingModal'
import MonthSummary from './MonthSummary'

function Table() {
	const habits = useAppSelector((state) => state.habits)
	const activeHabits = useAppSelector(selectActiveHabits)
	const isLoadingHabits = useAppSelector((state) => state.loading)
	const hasOnboarded = useAppSelector(state => state.auth.user?.result.hasOnboarded)

	const user = useAppSelector(state => state.auth)

	const dispatch = useAppDispatch()

	const isSolidified = (habit: Habit) => habit.colors[0] === SUCCESS_FINISH_COLOR
	// favorites get their own table, whether they are solidified or not
	const favoriteHabits = activeHabits.filter((habit) => habit.isFavorite)
	const otherHabits = activeHabits.filter((habit) => !habit.isFavorite)
	const ongoingHabits = otherHabits.filter((habit) => !isSolidified(habit))
	const finishedHabits = otherHabits.filter(isSolidified)

	const isSummaryShown = !isLoadingHabits && habits.length > 0

	let daysArray: number[] = []
	makeDaysArray(daysArray)

	async function handleSubmitHabit(habitName: string) {
		const todayKey = getTodayKey()
		const newHabit: StoredHabit = {
			name: habitName,
			_id: uuidv4(),
			createdOn: todayKey,
			startDate: todayKey,
			log: {},
		}
		await postHabit(newHabit)
		dispatch(habitsActions.add({ habit: newHabit, todayKey }))
	}

	function onDeleteHabit(deleteIndex: number) {
		dispatch(habitsActionsThunk.deleteHabitAction(deleteIndex))
	}

	function createHabitRow(habit: Habit, index: number, isFinished: boolean) {
		return <HabitRow habitObject={habit} isFinished={isFinished} key={habit._id} delete={onDeleteHabit} />
	}

	function makeDaysHtml(day: number) {
		return (
			<th className="table-day-cell" key={day}>
				<span className="days-small">Day</span>
				<br />
				{day}
			</th>
		)
	}

	async function onFinishOnboarding(){
		try {
			if(!user) {
				return
			}
			dispatch(authActions.onboard())
			const result = await onboard()
			const updatedUser = {...user.user, result}
			localStorage.setItem('User', JSON.stringify(updatedUser))
		} catch (error) {
			console.log(error)
		}
	}

	return (
		<div className="container relative">
			<div className="background-div">
				<BackgroundSVG />
			</div>

			{hasOnboarded === false && <OnboardingModal onFinishOnboarding={onFinishOnboarding} />}

			{isSummaryShown && <MonthSummary />}

			{favoriteHabits.length > 0 && (
				<table className={`habit-table ${isSummaryShown ? 'habit-table-after-summary' : ''}`}>
					<thead>
						<tr className="table-first-row">
							<th className="table-habit-title-cell">
								<h4 className="table-habit-title">Favorite Habits</h4>
							</th>
							{daysArray.map(makeDaysHtml)}
						</tr>
					</thead>

					<tbody>{favoriteHabits.map((habit, index) => createHabitRow(habit, index, isSolidified(habit)))}</tbody>
				</table>
			)}

			<table className={`habit-table ${isSummaryShown && favoriteHabits.length === 0 ? 'habit-table-after-summary' : ''}`}>
				<thead>
					<tr className="table-first-row">
						<th className="table-habit-title-cell">
							<h4 className="table-habit-title">Habits</h4>
						</th>
						{daysArray.map(makeDaysHtml)}
					</tr>
				</thead>

				<tbody>
					{isLoadingHabits && <TableSkeleton />}
					{ongoingHabits.map((habit, index) => createHabitRow(habit, index, false))}
					<NewHabitForm handleSubmitHabit={handleSubmitHabit} />
				</tbody>
			</table>

			{finishedHabits.length > 0 && (
				<table className="habit-table">
					<thead>
						<tr className="table-first-row">
							<th className="table-habit-title-cell">
								<h4 className="table-habit-title">Solidified Habits</h4>
							</th>
							{daysArray.map(makeDaysHtml)}
						</tr>
					</thead>

					<tbody>{finishedHabits.map((habit, index) => createHabitRow(habit, index, true))}</tbody>
				</table>
			)}
		</div>
	)
}

export default Table
